import type { Ref } from 'vue'
import type { EventDateType } from '~/composables/types/event'
import {
  useDateRequestFormStorage,
  type DateRequestFormData,
} from '~/composables/useDateRequestFormStorage'

export interface DateRequestFormModel {
  email: string
  phone: string
  name: string
  surname: string
  numberOfTickets: number | null
  companyName: string
  city: string
  dateType: EventDateType | null
  dates: string[]
  additionalInfo?: string
}

const createEmptyFormModel = (): DateRequestFormModel => ({
  email: '',
  phone: '',
  name: '',
  surname: '',
  numberOfTickets: null,
  companyName: '',
  city: '',
  dateType: null,
  dates: [],
  additionalInfo: '',
})

const normalizeDateType = (
  value: DateRequestFormData['dateType']
): EventDateType | null =>
  value === 'PRIVATE' || value === 'PUBLIC' ? value : null

/**
 * Owns the date-request form ref and a single localStorage hydration path.
 * Ticket count is never restored from persisted data (see applySavedData).
 */
export const useDateRequestFormState = (): {
  form: Ref<DateRequestFormModel>
} => {
  const form = ref<DateRequestFormModel>(createEmptyFormModel())

  const { savedFormData, loaded } = useDateRequestFormStorage()

  const applySavedData = (data: DateRequestFormData) => {
    form.value = {
      ...createEmptyFormModel(),
      ...data,
      numberOfTickets: null,
      dateType: normalizeDateType(data.dateType),
      dates: [...(data.dates ?? [])],
    }
  }

  // Single hydration: fires once when localStorage finishes loading, and
  // again only if `savedFormData` is reassigned (no deep watch).
  watch(
    [loaded, savedFormData],
    ([isLoaded, data]) => {
      if (!isLoaded || !data) {
        return
      }
      applySavedData(data)
    },
    { immediate: true }
  )

  return {
    form,
  }
}
